/**
 * Main Menu View
 *
 * System menu for navigating the application.
 * Uses DopeUI for consistent terminal-green styling.
 */

import { registerView } from '../viewRegistry';
import { interactiveRegistry } from '../interactivity';
import { getCanonicalSafeArea } from '../safeArea';
import { renderBlobBackground } from '../renderer/plasma';
import { getCurrentUserSync } from '../auth/authService';
import { PLATFORM } from '../device';
import { getAddress } from 'ethers';
import DopeUI, { TERMINAL, SPACING } from '../ui/DopeOS-UI';

function abbreviateAddress(addr) {
  if (!addr) return '';
  let checksum = addr;
  try {
    checksum = getAddress(addr);
  } catch {
    // ignore invalid address
  }
  if (checksum.length <= 10) return checksum;
  return `${checksum.slice(0, 6)}...${checksum.slice(-4)}`;
}

// Track hover state for menu items
const menuState = {
  hoveredItem: null
};

registerView('main_menu', function renderMainMenu(
  ctx,
  time,
  width,
  height,
  params
) {
  // Clear interactions at start of frame
  interactiveRegistry.clear();

  const w = width || 640;
  const h = height || 400;
  const safe = getCanonicalSafeArea({ w, h });

  // Clear background
  ctx.fillStyle = TERMINAL.BLACK;
  ctx.fillRect(0, 0, w, h);

  // Plasma background
  ctx.save();
  ctx.globalAlpha = 0.2;
  renderBlobBackground(ctx, time, w, h);
  ctx.restore();

  // Header panel with title
  DopeUI.Panel(ctx, safe.left, safe.top, safe.width, 70, 'DOPE CHAIN WARS');

  // Subtitle
  DopeUI.Text(ctx, 'SYSTEM MENU', safe.left + SPACING.MD, safe.top + 45, 'dim', 1.2);

  // User identity panel (top right)
  const user = getCurrentUserSync();
  let identityLine = 'WALLET LINKED';
  let subLine = '';

  if (user) {
    if (user.platform === PLATFORM.FARCASTER) {
      identityLine = 'FARCASTER LINKED';
      if (user.username) subLine = `@${user.username}`;
      else if (user.fid) subLine = `FID: ${user.fid}`;
      else if (user.address) subLine = abbreviateAddress(user.address);
    } else if (user.platform === PLATFORM.WORLDCOIN) {
      identityLine = 'WORLD ID LINKED';
      subLine = 'Verified Human';
    } else {
      identityLine = 'WALLET LINKED';
      if (user.ensName) subLine = user.ensName;
      else if (user.address) subLine = abbreviateAddress(user.address);
    }
  } else if (params && params.address) {
    if (params.ens) subLine = params.ens;
    else subLine = abbreviateAddress(params.address);
  }

  // User info panel
  const userPanelWidth = 200;
  const userPanelX = safe.right - userPanelWidth - SPACING.MD;
  DopeUI.Panel(ctx, userPanelX, safe.top + 80, userPanelWidth, 60);

  DopeUI.Text(ctx, identityLine, userPanelX + SPACING.SM, safe.top + 100, 'bright', 0.9);
  if (subLine) {
    DopeUI.Text(ctx, subLine, userPanelX + SPACING.SM, safe.top + 120, 'dim', 0.8);
  }

  // Menu items using Grid layout
  const menuItems = [
    { id: 'talk-shark', text: 'TALK TO A SHARK', action: 'talk' },
    { id: 'economy', text: 'ECONOMY', action: 'economy', view: 'economy_dashboard' },
    { id: 'leaderboard', text: 'LEADERBOARD', action: 'leaderboard' },
    { id: 'about', text: 'ABOUT', action: 'about' },
    { id: 'disconnect', text: 'DISCONNECT', action: 'disconnect' }
  ];

  // Responsive grid sizing
  const isMobile = safe.width < 400;
  const btnWidth = Math.min(280, safe.width * (isMobile ? 0.9 : 0.5) - SPACING.MD * 2);
  const btnHeight = isMobile ? 45 : 35;
  const btnGap = isMobile ? 12 : 10;

  const grid = DopeUI.Grid(
    safe.left + SPACING.MD,
    safe.top + 80,
    1,
    menuItems.length,
    btnWidth,
    btnHeight,
    btnGap
  );

  // Check for hover (from params if available)
  const mouseX = params?.mouseX || -1;
  const mouseY = params?.mouseY || -1;

  menuItems.forEach((item, index) => {
    const cell = grid.cell(0, index);

    // Determine button state
    let btnState = 'normal';
    const isHovered = mouseX >= cell.x && mouseX <= cell.x + cell.width &&
                      mouseY >= cell.y && mouseY <= cell.y + cell.height;

    if (isHovered) {
      btnState = 'hover';
      menuState.hoveredItem = item.id;
    }

    // Draw as button using DopeUI
    DopeUI.Button(ctx, cell.x, cell.y, cell.width, cell.height, item.text, btnState);

    // Register interaction
    interactiveRegistry.register({
      id: item.id,
      x: cell.x,
      y: cell.y,
      w: cell.width,
      h: cell.height,
      onClick: () => {
        if (params) {
          if (item.action === 'talk' && typeof params.onTalkClick === 'function') {
            params.onTalkClick();
            return;
          }
          if (item.action === 'leaderboard' && typeof params.onLeaderboardClick === 'function') {
            params.onLeaderboardClick();
            return;
          }
          if (item.action === 'about' && typeof params.onAboutClick === 'function') {
            params.onAboutClick();
            return;
          }
          if (item.action === 'disconnect' && typeof params.onDisconnectClick === 'function') {
            params.onDisconnectClick();
            return;
          }
        }

        if (item.view) {
          if (typeof window !== 'undefined' && window.DCW_setView) {
            window.DCW_setView(item.view);
          }
        }
      }
    });
  });

  // Footer panel
  DopeUI.Panel(ctx, safe.left, safe.bottom - 30, safe.width, 30);
  DopeUI.Text(ctx, '© 2024-2026 DOPE CHAIN WARS', safe.left + SPACING.MD, safe.bottom - 12, 'dim', 0.9);
});

export default {
  viewName: 'main_menu',
};
